import * as React from 'react'
import { List } from 'immutable'
import { Widget, fromJSX, Unit, any } from 'widgets-for-react'
import { BreadcrumbItemProps } from '../../../../site/utils/breadcrumbs'
import {
  fakeIntro,
  fakeTitleShort,
  imgPlaceholder
} from '../../config/fakeData'
import { StyleguideExample, StyleguidePart } from '../../utils/renderers'
import { FakeBreadcrumbs } from '../objects/breadcrumbs'
import {
  ComponentButtonPrimary,
  ComponentButtonSecondary
} from '../objects/buttons'
import { ArticlePageHeader } from '../../../../site/root/cmsPage/pages/article'
import { formatDateDDMMYYYY } from '../../../../site/utils/date'

// ---------- ---------- ---------- ---------- ---------- ----------
// ---------- ---------- ---------- ---------- ---------- ----------
// ---------- ----------  STYLEGUIDE RENDERER  ---------- ----------
// ---------- ---------- ---------- ---------- ---------- ----------
// ---------- ---------- ---------- ---------- ---------- ----------

export const ComponentsPageheaders = (): Widget<Unit> =>
  any<Unit>([
    StyleguidePart({ title: 'With image - Fullscreen', intro: '' })(
      StyleguideExample({ showExampleLabel: false, isFullScreen: false })(
        fromJSX(_ => (
          <>
            {ComponentPageheader(true, true, {
              breadcrumb: List([
                { url: '/', label: 'Evofenedex' },
                { url: '/', label: 'Page' },
                { url: '/', label: 'SubPage' }
              ]),
              title: fakeTitleShort,
              intro: fakeIntro,
              meta: (
                <>
                  <li className="pageheader__metaitem">
                    <time>01 april 2021</time>
                  </li>
                  <li className="pageheader__metaitem">
                    Door Willem Wereldwijd
                  </li>
                </>
              ),
              primaryButton: true,
              secondaryButton: true
            }).run(_)}
          </>
        ))
      )
    ),
    StyleguidePart({ title: 'With image', intro: '' })(
      StyleguideExample({ showExampleLabel: false, isFullScreen: false })(
        fromJSX(_ => (
          <>
            {ComponentPageheader(true, false, {
              breadcrumb: List([
                { url: '/', label: 'Evofenedex' },
                { url: '/', label: 'Page' },
                { url: '/', label: 'SubPage' }
              ]),
              title: fakeTitleShort,
              intro: fakeIntro
            }).run(_)}
          </>
        ))
      )
    ),
    StyleguidePart({ title: 'Without image', intro: '' })(
      StyleguideExample({ showExampleLabel: false, isFullScreen: false })(
        fromJSX(_ => (
          <>
            {ComponentPageheader(false, false, {
              breadcrumb: List([
                { url: '/', label: 'Evofenedex' },
                { url: '/', label: 'Page' },
                { url: '/', label: 'SubPage' }
              ]),
              title: fakeTitleShort,
              intro: fakeIntro,
              meta: (
                <>
                  <li className="pageheader__metaitem">
                    <time>01 april 2021</time>
                  </li>
                  <li className="pageheader__metaitem">
                    Door Willem Wereldwijd
                  </li>
                </>
              ),
              primaryButton: true,
              secondaryButton: true
            }).run(_)}
          </>
        ))
      )
    ),
    StyleguidePart({ title: 'Article pageheader - With Image', intro: '' })(
      StyleguideExample({ showExampleLabel: false, isFullScreen: false })(
        fromJSX(_ => <>{ComponentArticlePageHeader(true).run(_)}</>)
      )
    ),
    StyleguidePart({ title: 'Article pageheader - Without Image', intro: '' })(
      StyleguideExample({ showExampleLabel: false, isFullScreen: false })(
        fromJSX(_ => <>{ComponentArticlePageHeader(false).run(_)}</>)
      )
    )
  ])

// ---------- ---------- ---------- ---------- ---------- ----------
// ---------- ---------- ---------- ---------- ---------- ----------
// ---------- ----------        SNIPPETS       ---------- ----------
// ---------- ---------- ---------- ---------- ---------- ----------
// ---------- ---------- ---------- ---------- ---------- ----------

export const ComponentPageheader = (
  hasImage: boolean,
  fullsize?: boolean,
  values?: {
    label?: string
    breadcrumb?: List<BreadcrumbItemProps>
    title?: string
    intro?: string
    meta?: JSX.Element
    primaryButton?: boolean
    secondaryButton?: boolean
  }
): Widget<never> =>
  fromJSX(_ => (
    <header
      className={`pageheader pageheader--${
        hasImage ? 'image' : 'text'
      } pageheader--${fullsize ? 'fw' : 'cw'}`}
    >
      <div className="pageheader__inner">
        <div className="pageheader__content container--s gutter--m spacing--m">
          {values && values.label && (
            <p className="pageheader__label label--m">{values.label}</p>
          )}
          {values && values.breadcrumb && FakeBreadcrumbs(values.breadcrumb)}
          {values && values.title && (
            <h1 className="pageheader__title title--l">{values.title}</h1>
          )}
          {values && values.intro && (
            <p className="pageheader__intro">{values.intro}</p>
          )}
          {values && values.meta && (
            <ul className="pageheader__meta text--s">{values.meta}</ul>
          )}

          {values && (values.primaryButton || values.secondaryButton) && (
            <div className="pageheader__clickables">
              {values.primaryButton && (
                <div className="pageheader__clickable">
                  {ComponentButtonPrimary(false).run(_)}
                </div>
              )}

              {values.secondaryButton && (
                <div className="pageheader__clickable">
                  {ComponentButtonSecondary(false).run(_)}
                </div>
              )}
            </div>
          )}
        </div>

        {hasImage && (
          <div className="pageheader__visual" aria-hidden="true">
            <div
              className={`${
                fullsize ? 'pageheader__figure' : 'container--m gutter--m'
              }`}
            >
              <div className="pageheader__media">
                <img
                  className="pageheader__image"
                  src={
                    fullsize
                      ? imgPlaceholder(1560, 600, '0', '#535054')
                      : imgPlaceholder(1040, 434)
                  }
                  alt=""
                  width={fullsize ? '1560' : '1040'}
                  height={fullsize ? '600' : '434'}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  ))

export const ComponentArticlePageHeader = (withImage: boolean): Widget<never> =>
  fromJSX(() => {
    const magazine = 'Globe'
    const title = fakeTitleShort

    return (
      <>
        <header className="articleph">
          <div className="articleph__visual" aria-hidden="true">
            {withImage ? (
              <div className="container--l gutter--m">
                <div className="articleph__media">
                  <img
                    src={imgPlaceholder(1240, 420)}
                    alt=""
                    width={'1240'}
                    height={'420'}
                  />
                  <svg
                    className="magazine-icon"
                    width="92px"
                    height="92px"
                    aria-hidden="true"
                  >
                    <use
                      xlinkHref={
                        magazine === 'Globe'
                          ? '#icon--corner-g'
                          : '#icon--corner-l'
                      }
                    />
                  </svg>
                </div>
              </div>
            ) : (
              <>
                <div className="icon-helper">
                  <div className="container--l gutter--m">
                    <div className="articleph__media">
                      <svg
                        className="magazine-icon"
                        width="56px"
                        height="76px"
                        aria-hidden="true"
                      >
                        <use
                          xlinkHref={
                            magazine === 'Globe'
                              ? '#icon--straight-g'
                              : '#icon--straight-l'
                          }
                        />
                      </svg>
                    </div>
                  </div>
                </div>
                <img
                  className="image-placeholder no-lazyload"
                  width="100%"
                  height="420"
                  alt=""
                  src="/images/article-header-placeholder-large.png"
                  data-src={'/images/article-header-placeholder-large.png'}
                  data-srcset={
                    '/images/article-header-placeholder-large.png 720w, /images/article-header-placeholder-large.png 1440w, /images/article-header-placeholder-large.png 2880w'
                  }
                  data-sizes="100vw"
                />
              </>
            )}
          </div>
          <div
            className={`articleph__container ${
              withImage ? 'container--with-image' : 'container--wo-image'
            }`}
          >
            <div className="articleph__inner">
              <p className="articleph__date">
                {formatDateDDMMYYYY(new Date())}
              </p>
              <h1 className="articleph__title title--l">{title}</h1>
              <span className="articleph__highlightedLetter">
                {title.slice(0, 1)}
              </span>
            </div>
          </div>
        </header>
      </>
    )
  })
